import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { confirmDialog } from "../lib/confirm";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
import { CustomerProfileHeader } from "../components/customer-detail/CustomerProfileHeader";
import { CustomerProfileEditModal } from "../components/customer-detail/CustomerProfileEditModal";
import { AdminPinPromptDialog } from "../components/admin/AdminPinPromptDialog";
import { AbsenceCard } from "../components/customer-detail/AbsenceCard";
import { CustomerInviteCard } from "../components/customer-detail/CustomerInviteCard";
import { CustomerPreferencesCard } from "../components/customer-detail/CustomerPreferencesCard";
import { CustomerAdminNoteCard } from "../components/customer-detail/CustomerAdminNoteCard";
import { ConnectedAccessSection } from "../components/customer-detail/ConnectedAccessSection";
import { ServicePackagesCard } from "../components/customer-detail/ServicePackagesCard";
import { BillingOverviewCard } from "../components/customer-detail/BillingOverviewCard";

/**
 * Feb 2026 · Kunden-Detailseite (Team-Sicht).
 *
 * User-Directive #302 (28 Feb 2026): Alles unterhalb der „Interne Anmerkung"
 * — Vorbereitungs-Fortschritt (Zeitstrahl), Vorbereitungs-Checkliste,
 * Zusatzleistungen, Objekte · Live-Status, „Kachel hinzufügen", Anfragen
 * (Live-Checkliste) und Einzelbuchungen-Liste — wurden aus dem Kundenprofil
 * entfernt. Diese Bereiche waren primär objektbezogen oder buchungsspezifisch
 * und werden künftig in einer dedizierten Objekt-Detailansicht abgebildet.
 *
 * Aktuelle Struktur (linear, in Reihenfolge):
 *   1. Header (Foto / Kontakt / Bank / Zugewiesene Objekte)
 *   2. Absence-Karte (aktuelle / nächste Abwesenheit)
 *   3. Kunden-Präferenzen (Allergien / Ernährung / Home-Setup)
 *   4. Interne Anmerkung (Admin-only, direkt unter Präferenzen)
 */
export default function TeamCustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { effectiveRole, user } = useAuth();
  const [data, setData] = useState(null);
  // Feb 2026 · Sicherheits-Gate: Bevor der Edit-Modal geöffnet wird, muss der
  // Admin eine 4-stellige PIN eingeben (Werksstandard `0611`, änderbar unter
  // `/team/settings/edit-pin`). Verhindert versehentliche Änderungen bei kurz
  // unbeobachteten Sessions.
  const [pinPromptOpen, setPinPromptOpen] = useState(false);
  const [profileEditOpen, setProfileEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/team/customers/${id}/detail`);
      setData(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      nav("/team/customers");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div
        className="max-w-[1100px] mx-auto px-4 md:px-10 py-10 flex items-center justify-center label-caps"
        data-testid="customer-detail-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird geladen …
      </div>
    );
  }

  const { customer, properties, current_absence, next_absence } = data;

  const forceLogout = async () => {
    const ok = await confirmDialog({
      title: "Von allen Geräten abmelden?",
      description: `${customer.name || customer.email} wird auf allen Geräten abgemeldet und muss sich neu anmelden. Der lokale Cache wird beim nächsten App-Aufruf gelöscht.`,
      confirmText: "Abmelden",
      cancelText: "Abbrechen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data: res } = await api.post(`/team/customers/${id}/logout-all`);
      toast.success("Kunde wurde von allen Geräten abgemeldet.");
      setData((d) => ({ ...d, customer: { ...d.customer, sessions_invalidated_at: res.sessions_invalidated_at } }));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className="max-w-[1100px] mx-auto px-4 md:px-10 py-8 md:py-12 pb-24"
      data-testid="customer-detail"
    >
      {/* Back link */}
      <button
        type="button"
        onClick={() => nav("/team/customers")}
        data-testid="customer-detail-back"
        className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy mb-4"
      >
        <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> Zurück zur Liste
      </button>

      {/* Feb 2026 · Read-only Header + separater Bearbeiten-Flow.
          Vorher: viele inline-Editoren (Birthday, Membership, …). Jetzt:
          alles auf einen Blick, Änderungen ausschließlich über den
          „Bearbeiten"-Button → PIN-Prompt → `CustomerProfileEditModal`. */}
      <CustomerProfileHeader
        customer={customer}
        properties={properties}
        canEdit={effectiveRole === "admin"}
        onEditClick={() => setPinPromptOpen(true)}
      />
      {pinPromptOpen && (
        <AdminPinPromptDialog
          title="Kundenprofil bearbeiten"
          description="Bitte den 4-stelligen Bearbeitungs-PIN eingeben, um die Stammdaten zu ändern."
          onClose={() => setPinPromptOpen(false)}
          onSuccess={() => {
            setPinPromptOpen(false);
            setProfileEditOpen(true);
          }}
        />
      )}
      {profileEditOpen && (
        <CustomerProfileEditModal
          customerId={id}
          initial={customer}
          onClose={() => setProfileEditOpen(false)}
          onSaved={(fresh) => setData((d) => ({
            ...d,
            customer: { ...d.customer, ...fresh },
          }))}
        />
      )}

      {/* Absence status — prominent */}
      <div className="mb-6">
        <AbsenceCard current={current_absence} next={next_absence} />
      </div>

      {/* Service-Paket pro Objekt — reine Admin-/Team-Einsicht (keine
          Änderung). Buchungen sind objektspezifisch, daher pro Immobilie. */}
      <ServicePackagesCard customerId={id} properties={properties} canCancel={effectiveRole === "admin"} />

      {/* Abrechnungsübersicht — reine Lese-Vorlage für die manuelle sevDesk-
          Rechnung (pro Objekt: Paket + Abwesenheiten + Zusatzleistungen). */}
      <BillingOverviewCard customerId={id} properties={properties} />

      {/* Feb 2026 · 27 Feb 2026 · User-Directive „Als eigene Karte direkt
          im Kunden-Profil (unter Absence)": Kundennummer + Rechnungs-
          Kennzahlen als Admin-Karte. Inline-Edit mit PIN-Bestätigung.
          Wird auch dann angezeigt, wenn (noch) keine Nummer gepflegt ist —
          absichtlich, damit Admins die Pflege nicht vergessen. */}
      {effectiveRole === "admin" && (
        <CustomerInviteCard
          customer={customer}
          onSent={(res) => setData((d) => ({
            ...d,
            customer: { ...d.customer, invite_sent_at: res.invite_sent_at, invite_sent_by: user?.name || user?.email },
          }))}
        />
      )}

      {effectiveRole === "admin" && (
        <div className="mt-6 bg-white border border-navy/10 p-5" data-testid="force-logout-card">
          <div className="label-caps mb-2">Sitzungen</div>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-3">
            Meldet diesen Kunden auf allen Geräten ab. Beim nächsten Aufruf der App erscheint der
            Login-Bildschirm und der lokale Cache wird gelöscht.
          </p>
          {customer.sessions_invalidated_at && (
            <p className="text-[12px] text-navy-dark/50 mb-3" data-testid="force-logout-last">
              Zuletzt erzwungen: {fmtDateTime(customer.sessions_invalidated_at)}
            </p>
          )}
          <button
            type="button"
            onClick={forceLogout}
            data-testid="force-logout-btn"
            className="text-[11px] tracking-[0.18em] uppercase px-5 py-2.5 border border-[#B42318] text-[#B42318] hover:bg-[#B42318] hover:text-white transition-colors"
          >
            Von allen Geräten abmelden
          </button>
        </div>
      )}

      {/* Kunden-Präferenzen (Allergien / Ernährung / Home-Setup) —
          read-only Anzeige direkt unter der Absence-Karte, damit der
          Concierge sie vor der nächsten Vorbereitung im Blick hat. */}
      <CustomerPreferencesCard customerId={id} />

      {/* Feb 2026 · Verbundene Zug\u00e4nge (5 Sep 2026 · User-Directive):
          Familie + Partner-Kopplungen. Bidirektional \u2014 auf der
          Zugangsliste (`/team/allowed`) sehen die Familie/Partner-Zeilen
          umgekehrt den Hauptkunden. */}
      <ConnectedAccessSection customerId={id} />

      {/* Feb 2026 · Directive #299 · Interne Anmerkung direkt unter den
          Präferenzen — nur für Admins sichtbar. Backend liefert die Notiz
          für Concierges gar nicht erst aus (null), sodass diese Karte auch
          bei client-side Renderern nicht flackert. */}
      {effectiveRole === "admin" && (
        <CustomerAdminNoteCard note={customer.admin_note} />
      )}
    </div>
  );
}
